import React, { useEffect, useState, useReducer } from "react";
import { observer } from "mobx-react";
import Button from "Components/common/Button";
import NumberInputBox from "Components/common/NumberInputBox";
import { whatsAppReplyState } from "./state";
import WhatsAppProfileCard from "./common/WhatsAppProfileCard";
import makeApi, { URLS } from "Utils/apiURL";
import { toasterState } from "Components/common/toaster";
import userDetail from "Stores/userDetail";

const initialState = {
  maxMessagesPerDay: 50,
  intervalInSeconds: 120
};

const formReducer = (state = {}, action = {}) => {
  const { type = "", payload = {}, value = "", field = "" } = action || {};
  switch (type) {
    case "SET_ENTIRE_STATE":
      return payload;
    case "CHANGE_FIELD":
      return {
        ...state,
        [field]: value
      };
    default:
      return state;
  }
};

const ConnectedWhatsAppConfigure = observer(() => {
  const [state, dispatch] = useReducer(formReducer, initialState);
  const [errors, setErrors] = useState({});
  const [saveLoader, setSaveLoader] = useState(false);

  const planDailyLimit = userDetail?.userFeatureFlag?.waMaxMessagesPerDay || 0;

  const handleChange = (value = "", field = "") => {
    dispatch({ type: "CHANGE_FIELD", field, value });
  };

  const checkError = () => {
    const newErrors = {};
    let hasError = false;
    const maxMessages = parseInt(state.maxMessagesPerDay, 10);
    if (isNaN(maxMessages) || maxMessages < 0) {
      newErrors.maxMessagesPerDay = "Max messages per day cannot be negative";
      hasError = true;
    } else if (maxMessages > planDailyLimit) {
      newErrors.maxMessagesPerDay = `Max messages per day cannot exceed your plan limit of ${planDailyLimit}`;
      hasError = true;
    }
    const interval = parseInt(state.intervalInSeconds, 10);
    if (isNaN(interval) || interval < 30) {
      newErrors.intervalInSeconds = "Interval must be at least 30 seconds";
      hasError = true;
    }
    setErrors(newErrors);
    return hasError;
  };

  const saveSettings = async () => {
    setSaveLoader(true);
    const config = {
      url: URLS?.saveSettingsInWhatsApp,
      method: "POST",
      data: {
        accountId: whatsAppReplyState?.configureData?.id,
        maxMessagesPerDay: parseInt(state.maxMessagesPerDay, 10) || 0,
        intervalInSeconds: parseInt(state.intervalInSeconds, 10) || 0
      }
    };
    const response = await makeApi(config);
    const { responseCode = "" } = response?.data || {};
    if (+responseCode === 200) {
      toasterState.setToastMsg("WhatsApp settings saved successfully.", "success");
      whatsAppReplyState.setConfigureData({
        ...whatsAppReplyState.configureData,
        settings: {
          ...(whatsAppReplyState.configureData?.settings || {}),
          maxMessagesPerDay: parseInt(state.maxMessagesPerDay, 10) || 0,
          intervalInSeconds: parseInt(state.intervalInSeconds, 10) || 0
        }
      });
      whatsAppReplyState.getWhatsappAccounts();
    } else if (+responseCode === 401) {
      toasterState.setToastMsg("You don't have access to WhatsApp settings.", "failure");
    } else {
      toasterState.setToastMsg("Something went wrong, please try again later", "failure");
    }
    setSaveLoader(false);
  };

  const handleSave = () => {
    if (!checkError()) {
      saveSettings();
    }
  };

  useEffect(() => {
    const { settings = {} } = whatsAppReplyState?.configureData || {};
    if (settings && Object.keys(settings).length > 0) {
      dispatch({
        type: "SET_ENTIRE_STATE",
        payload: { ...initialState, ...settings }
      });
    } else {
      dispatch({
        type: "SET_ENTIRE_STATE",
        payload: { ...initialState, maxMessagesPerDay: planDailyLimit }
      });
    }
  }, [whatsAppReplyState?.configureData]);

  useEffect(() => {
    return () => {
      whatsAppReplyState.setShowConfigure(false);
    };
  }, []);

  const buttonList = [
    {
      id: "back",
      name: "back",
      btnText: "Back",
      type: "button",
      className: "cancelButton",
      click: () => {
        whatsAppReplyState.getWhatsappAccounts();
        whatsAppReplyState.setShowConfigure(false);
      }
    },
    {
      id: "save",
      name: "save",
      btnText: "Save",
      type: "button",
      className: `saveButton ${saveLoader ? "loading" : ""}`,
      click: handleSave,
      loader: saveLoader
    }
  ];

  return (
    <div className="connectedLinkedInConfigure">
      <div className="sectionHeader">
        <div className="backBtnAndHeader">
          <span
            className="navigateToInbox"
            onClick={() => whatsAppReplyState.setShowConfigure(false)}
          >
            <i className="material-icons backBtn">keyboard_backspace</i>
          </span>
          <h6>Connected Accounts</h6>
        </div>
      </div>
      <div className="settingsWrapper">
        <WhatsAppProfileCard
          {...whatsAppReplyState?.configureData}
          showTitle={true}
        />
        <div className="linkedInSettings">
          <div className="warmingSettingsCont">
            <div className="warmingSettingsHeader">WhatsApp Account Settings</div>
            <div className="warmingSettingsBody">
              <NumberInputBox
                type="number"
                changeCbk={value => handleChange(value, "maxMessagesPerDay")}
                min={0}
                max={planDailyLimit}
                value={state.maxMessagesPerDay}
                errMsg={errors.maxMessagesPerDay}
                loader={false}
                readOnly={false}
                label="Max messages per day"
                subLabel="(Recommended: 50)"
              />
              <NumberInputBox
                type="number"
                changeCbk={value => handleChange(value, "intervalInSeconds")}
                min={30}
                max={3600}
                value={state.intervalInSeconds}
                errMsg={errors.intervalInSeconds}
                loader={false}
                readOnly={false}
                label="Interval between messages (seconds)"
                subLabel="(Recommended: 120, Min: 30)"
              />
            </div>
            <div className="buttonActionList">
              {buttonList.map(item => (
                <Button {...item} key={`${item?.id}Btn`} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export { ConnectedWhatsAppConfigure };
export default ConnectedWhatsAppConfigure;
